import { ChangeDetectionStrategy, Component, computed, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { DmsDocumentDeleteService } from './dms-document-delete.service';
import { DmsLinkEntityType, entityTypeLabel } from '../../types';

/**
 * DMS-spezifischer Lösch-Wahl-Dialog (Spec §7.2/§7.3).
 *
 * Selector = Host-Selector: die Komponente konsumiert das DmsDocumentDeleteService-
 * Signal direkt und wird einmalig im Default-Layout gemountet (wie die
 * T2d*-Dialog-Hosts). Optik am t2d-delete-dialog-Tree-Look orientiert.
 *
 * - entityType === null  → nur „Dokument löschen" (globale Liste / Detailseite)
 * - entityType gesetzt   → beide Modi; „Verknüpfung lösen" disabled bei isLastLink
 */
@Component({
  selector: 't2d-dms-document-delete-dialog-host',
  standalone: true,
  imports: [CommonModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './dms-document-delete-dialog.component.html',
  styleUrl: './dms-document-delete-dialog.component.scss',
})
export class DmsDocumentDeleteDialogComponent {
  /** Feminine entity types that require the dative article "dieser" (not "diesem"). */
  private readonly FEMININE_TYPES = new Set<DmsLinkEntityType>(['organisation', 'leistungsphase', 'teilleistung']);

  protected readonly service = inject(DmsDocumentDeleteService);

  protected readonly req = this.service.request;

  /** Konkretes Typ-Label des Bezugstyps (oder null in globaler Liste). */
  protected readonly typeLabel = computed(() => {
    const r = this.req();
    return r?.entityType ? entityTypeLabel(r.entityType) : null;
  });

  /** „Verknüpfung zu dieser/diesem <Typ> lösen" — article declines by grammatical gender. */
  protected readonly unlinkLabel = computed(() => {
    const r = this.req();
    const t = this.typeLabel();
    if (!t) return null;
    const article = r?.entityType && this.FEMININE_TYPES.has(r.entityType) ? 'dieser' : 'diesem';
    return `Verknüpfung zu ${article} ${t} lösen`;
  });

  protected readonly tree = computed(() => this.req()?.preview.tree ?? []);

  protected onUnlink(): void {
    const r = this.req();
    if (!r || r.entityType === null || r.isLastLink) return;
    this.service.resolve('unlink');
  }

  protected onDelete(): void {
    this.service.resolve('delete');
  }

  protected onCancel(): void {
    this.service.resolve(null);
  }

  protected onBackdropClick(event: MouseEvent): void {
    if ((event.target as HTMLElement).classList.contains('dms-delete__backdrop')) {
      this.onCancel();
    }
  }
}
